// Choix de la VIGNETTE adaptée à la taille d'affichage (« économie de données », 16/07).
//
// POURQUOI CE FICHIER EST SÉPARÉ DE resolveImageUrl :
// resolveImageUrl est exporté et partagé — photoComposer.js s'en sert pour
// composer les photos 1080×1080 envoyées aux clientes sur WhatsApp. Y glisser
// une logique de vignette dégraderait CES photos-là. Règle : resolveImageUrl
// rend TOUJOURS l'original ; seul ce module choisit un dérivé, et uniquement
// là où on affiche petit.
//
// Les dérivés sont produits par l'API dans /uploads/d/<largeur>/<hash>.webp
// (services/imageDerivatives.service.js). L'original /uploads/<hash> reste
// intact et servi à l'identique — les APK figées v1.5→v1.13 en dépendent.

import config from '../../config';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN || '';

// Résolution d'URL UNIFIÉE — définie ICI (et non dans NdoeImage) pour éviter un
// cycle d'imports : NdoeImage a besoin de resolveImageVariant, qui a besoin de
// resolveImageUrl. NdoeImage la ré-exporte, donc l'API publique du design system
// (`import { resolveImageUrl } from '.../components/ndoe'`) ne change pas.
//
// Rend TOUJOURS l'original :
// - vide                 -> '' (déclenche le placeholder)
// - http/https/data/blob -> conservé tel quel (aperçus d'upload locaux)
// - chemin relatif       -> préfixé par le DOMAIN de l'API
export const resolveImageUrl = (src) => {
  if (!src) return '';
  const s = String(src).trim();
  if (!s) return '';
  return /^(https?:|data:|blob:)/i.test(s) ? s : `${DOMAIN}${s}`;
};

// Doit rester synchronisé avec LARGEURS de l'API.
export const LARGEURS = [96, 192, 384, 768];

// Au-delà de cette largeur nécessaire, l'original (~1254 px) est le meilleur
// choix : aucun dérivé ne ferait mieux (le hero mobile en densité 3 réclame
// ~1290 px physiques).
const PLUS_GRAND = LARGEURS[LARGEURS.length - 1];

/** Densité d'écran, bornée. */
const densite = () => {
  const d = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  // Plafonnée à 2 : au-delà, le gain visuel ne justifie plus le poids.
  return Math.min(Math.max(d, 1), 2);
};

/**
 * Le mode effectif : 'auto' est résolu ici, côté client.
 * - Sur l'APK (WebView Capacitor) et sur mobile → 'eco' : ce sont les forfaits
 *   data des livreurs, à l'origine du chantier.
 * - Sur navigateur de bureau → 'qualite' : le proprio garde la pleine résolution.
 * On respecte aussi « Économiseur de données » du navigateur/système s'il est
 * activé : l'utilisateur l'a déjà demandé au niveau de l'appareil.
 */
export const modeEffectif = (pref) => {
  if (pref === 'eco' || pref === 'qualite') return pref;
  if (typeof window === 'undefined') return 'qualite';
  try {
    const conn = navigator.connection || {};
    if (conn.saveData === true) return 'eco';
    const natif = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    if (natif) return 'eco';
    const petitEcran = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    if (petitEcran) return 'eco';
  } catch (e) { /* défensif : en cas de doute, qualité */ }
  return 'qualite';
};

/** Extrait le hash d'une URL d'upload. Rend null si ce n'est pas un upload. */
const hashDe = (src) => {
  const s = String(src || '').trim();
  if (!s || !s.startsWith('/uploads/')) return null;   // data:, blob:, http:, vide → pas de dérivé
  const nom = s.split('/').pop();
  if (!nom || nom.includes('.')) return null;          // déjà un fichier typé
  return nom;
};

/**
 * URL de la vignette adaptée, ou l'ORIGINAL si aucun dérivé ne convient.
 *
 * @param {string} src           valeur brute de la base (ex. '/uploads/abc123')
 * @param {number} largeurCssPx  largeur RÉELLE d'affichage à l'écran, en pixels CSS
 * @param {string} mode          'auto' | 'eco' | 'qualite'
 */
export const resolveImageVariant = (src, largeurCssPx, mode = 'auto') => {
  const original = resolveImageUrl(src);
  const hash = hashDe(src);
  if (!hash || !largeurCssPx) return original;

  const m = modeEffectif(mode);
  // En mode Éco on assume la densité 1 : l'image est très légèrement plus douce
  // sur un écran haute densité, en échange d'un gain de data majeur (c'est le
  // but explicite du réglage). En Qualité, on suit la densité réelle.
  const besoin = m === 'eco' ? largeurCssPx : largeurCssPx * densite();
  if (besoin > PLUS_GRAND) return original;

  const largeur = LARGEURS.find((w) => w >= besoin);
  return largeur ? `${resolveImageUrl('/uploads')}/d/${largeur}/${hash}.webp` : original;
};

/**
 * Jeu srcset pour les images FLUIDES (cartes de grille, hero) dont la largeur
 * dépend de la mise en page et de l'écran. À coupler avec un attribut `sizes`.
 * On laisse alors le NAVIGATEUR choisir : il connaît la largeur réelle rendue
 * ET la densité de l'écran, ce qu'aucun calcul JS ne fait de façon fiable —
 * c'est ce qui neutralise le piège des conteneurs qui écrasent la taille en CSS.
 * @returns {string|null} null si `src` n'est pas un upload (data:, blob:, http:)
 */
export const srcSetDe = (src, largeurMax = PLUS_GRAND) => {
  const hash = hashDe(src);
  if (!hash) return null;
  const base = resolveImageUrl('/uploads');
  // `largeurMax` sert au mode Éco : sans plafond, le navigateur choisirait
  // toujours le palier le plus net et le réglage n'aurait aucun effet.
  const retenues = LARGEURS.filter((w) => w <= largeurMax);
  if (!retenues.length) return null;
  return retenues.map((w) => `${base}/d/${w}/${hash}.webp ${w}w`).join(', ');
};

/**
 * Repli à utiliser dans onError : un dérivé peut manquer (backfill en cours,
 * photo trop petite pour ce palier, échec de génération). Il ne faut JAMAIS
 * tomber directement sur le placeholder — l'original, lui, existe toujours.
 * Chaîne : dérivé → original → placeholder.
 * @returns {string|null} l'URL originale si on n'y était pas déjà, sinon null.
 */
export const replique = (urlCourante, src) => {
  const original = resolveImageUrl(src);
  return urlCourante && original && urlCourante !== original ? original : null;
};
